import { Component, input, computed, effect, inject, OnDestroy, viewChild, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTab, MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { CommonModule, Location } from '@angular/common';
import { UsersGridComponent } from './users-grid/users-grid.component';
import { AgencyComponent } from './agency/agency.component';
import { AgencyStore } from '@program-suite/data-common';
import { ButtonComponent, ModalService, ModalType, SnackbarComponent, ToastType } from '@program-suite/ui';
import { AddUsersModalComponent } from '@program-suite/ui';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToolbarComponent } from '../../../../shared/ui-common/src/lib/components/toolbar/toolbar.component';

enum AgencyTabs {
  edit = 0,
  users = 1,
}

@Component({
  selector: 'lib-agency-users',
  imports: [CommonModule, UsersGridComponent, AgencyComponent, MatTabGroup, MatTab, ButtonComponent, ToolbarComponent],
  templateUrl: './agency-users.component.html',
  styleUrl: './agency-users.component.scss',
})
export class AgencyUsersComponent implements OnDestroy {
  agencyStore = inject(AgencyStore);
  snackBar = inject(MatSnackBar);
  tabGroup = viewChild<MatTabGroup>('tabGroup');
  baseUrl = '';
  tab: string | null = null;
  refresh = signal(false);

  private agencyIdParam = signal<string | null>(null);
  agencyId = computed(() => {
    const param = this.agencyIdParam();
    return param ? +param : null;
  });

  agencyName = computed(() => this.agencyStore.agencyDetails()?.agencyName);

  toolbarTiles = computed(() => this.agencyStore.toolbarTiles() || []);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private modalService: ModalService,
  ) {
    effect(() => {
      const id = this.agencyId();
      if (id) {
        this.agencyStore.getAgency(id);
      }
    });

    effect(() => {
      const id = this.agencyId();
      if (id) {
        this.agencyStore.getAgencyToolbarTiles(id);
      }
    });

    effect(() => {
      const refresh = this.refresh();
      if (refresh) {
        setTimeout(() => this.refresh.set(false), 0);
      }
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.agencyIdParam.set(params['agencyId'] || null);
    });

    this.tab = this.route.snapshot.paramMap.get('tab');
    this.baseUrl = `${this.route.snapshot.url[0].path}`;
    if (!this.tab) {
      this.tab = 'edit';
      this.updateUrl('edit');
    }
    this.navToNewTab(this.tab as keyof typeof AgencyTabs);
  }

  navToNewTab(tab: keyof typeof AgencyTabs): void {
    const tabGroup = this.tabGroup();
    if (!tabGroup) {
      return;
    }
    tabGroup.selectedIndex = AgencyTabs[tab as keyof typeof AgencyTabs];
  }

  onTabChange(event: MatTabChangeEvent): void {
    const newTab = AgencyTabs[event.index];
    const tabUrl = `${this.baseUrl}/${newTab}`;
    this.location.go(tabUrl, this.agencyId ? `agencyId=${this.agencyId()}` : undefined);
  }

  updateUrl(tab: string): void {
    const newUrl = `${this.baseUrl}/${tab}`;
    // Preserve the agencyId as a query parameter
    this.location.go(newUrl, this.agencyId ? `agencyId=${this.agencyId()}` : undefined);
  }

  goBack() {
    this.router.navigate(['/agencies']);
  }

  toolbarClick(tile: any) {
    throw new Error(`Action not implemented for: ${tile.label}`);
  }

  addUsers() {
    const modalOptions = {
      modalType: ModalType.FullScreen,
      data: {
        agencyId: this.agencyId(),
      },
    };

    this.modalService.open(AddUsersModalComponent, modalOptions, (result) => {
      this.agencyStore
        .inviteUsers(result)
        .then(() => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: {
              toastType: ToastType.Success,
              title: 'Users Invited',
              message: 'The agency users have been sent an email invitation.',
            },
          });
        })
        .catch((error) => {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 3000,
            data: { toastType: ToastType.Error, title: 'Error Inviting Users', message: error.message },
          });
        })
        .finally(() => {
          this.refresh.set(true);
        });
    });
  }

  refreshGrid(refreshGrid: boolean) {
    if (refreshGrid) {
      this.refresh.set(true);
    }
  }

  ngOnDestroy(): void {
    this.agencyStore.reset();
  }
}
